import { ISearchBarWidgetData, ISearchBarWidgetStyle, SearchBarLayoutType, SearchBarWidgetData } from "@pageBuilder/typings";
import { IRenderingCommonContext } from "@commonRendering/types";
import { StringUtil } from "src/utils/string-util";
import { CssProperties } from "../../../models/css-property";
import { WidgetRendererBase } from "../widget-renderer-base";
import { SearchBarScriptRenderer } from "./search-bar-script.renderer";

export class SearchBarWidgetRenderer extends WidgetRendererBase<ISearchBarWidgetData> {

    private readonly scriptRenderer: SearchBarScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new SearchBarScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("searchBarWidget");
    }

    protected renderInternal(element: HTMLElement, data: ISearchBarWidgetData): HTMLElement {
        this.renderBase(element, data);
        this.setAttributes(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        this.renderStyles(element, data);
        return element;
    }

    private renderBase(element: HTMLElement, data: ISearchBarWidgetData): void {
        const content = element.querySelector<HTMLElement>(".widget-content");
        let form = content?.querySelector("form");
        if (!form) {
            form = this.createForm(data);
            content?.appendChild(form);
        }

        const input = form.querySelector("input");
        if (input) {
            input.placeholder = data.placeholder;
        }
    }

    private createForm(data: ISearchBarWidgetData): HTMLFormElement {
        const form = document.createElement("form");
        form.classList.add("search-form");
        form.method = "POST";
        const field = this.createFormField();
        const input = this.createFormInput();
        const searchSpan = this.createFormSpan();

        this.setLayoutStyle(data.layout, field);
        field.appendChild(searchSpan);
        field.appendChild(input);
        form.appendChild(field);

        return form;
    }

    private createFormField(): HTMLDivElement {
        const field = document.createElement("div");
        field.classList.add("search-field");
        return field;
    }

    private createFormInput(): HTMLInputElement {
        const input = document.createElement("input");
        input.classList.add("search-control");
        input.setAttribute("tabindex", "-1");
        input.type = "text";
        input.value = "";
        input.autocomplete = "off";
        return input;
    }

    private createFormSpan(): HTMLSpanElement {
        const span = document.createElement("span");
        span.classList.add("search-icon");
        span.setAttribute("tabindex", "-1");
        return span;
    }

    private setAttributes(element: HTMLElement, data: ISearchBarWidgetData): void {
        if (data.quickSearch) {
            element.dataset.quickSearch = "";
        }
        else {
            delete element.dataset.quickSearch;
        }

        element.dataset.placeholder = data.placeholder;
    }

    protected renderStyles(element: HTMLElement, data: ISearchBarWidgetData): void {
        this.renderBaseStyles(element, data.style);
        this.renderLayoutStyle(element, data);
        this.setStyleVariables(element, data);
    }

    protected renderBaseStyles(element: HTMLElement, style: ISearchBarWidgetStyle): void {
        this.animationRenderer.setAnimationStyles(element, style.animation);
        const elementHeight = style.heightData?.height ? `${style.heightData.height}px` : "auto";
        element.style.height = elementHeight;
        element.style.minHeight = elementHeight;
    }

    protected renderLayoutStyle(element: HTMLElement, data: ISearchBarWidgetData): void {
        const field = element.querySelector<HTMLDivElement>(".search-field");

        if (!field) {
            return;
        }

        this.resetLayoutSetting(field);
        this.setLayoutStyle(data.layout, field);
    }

    protected setStyleVariables(element: HTMLElement, data: ISearchBarWidgetData): void {
        const cssProperties = new CssProperties();

        this.setButtonColorVariables(cssProperties, data);
        this.setInputColorVariables(cssProperties, data);

        this.styleService.setStyle(element, `#${element.id} .search-field`, cssProperties.data);
    }

    private setButtonColorVariables(cssProperties: CssProperties, data: ISearchBarWidgetData): void {
        const defaultLayoutStyle = SearchBarWidgetData.getDefaultLayoutStyle(data.layout);

        const iconColor =
            data.style.inputStyle.iconColor &&
            !StringUtil.isEmptyOrWhiteSpace(data.style.inputStyle.iconColor)
                ? data.style.inputStyle.iconColor
                : defaultLayoutStyle?.inputStyle.iconColor;

        const buttonColorDefault =
            data.style.inputStyle.buttonColor?.colorDefault &&
            !StringUtil.isEmptyOrWhiteSpace(data.style.inputStyle.buttonColor.colorDefault)
                ? data.style.inputStyle.buttonColor.colorDefault
                : defaultLayoutStyle?.inputStyle.buttonColor?.colorDefault;

        const buttonColorHovered =
            data.style.inputStyle.buttonColor?.colorHovered &&
            !StringUtil.isEmptyOrWhiteSpace(data.style.inputStyle.buttonColor.colorHovered)
                ? data.style.inputStyle.buttonColor.colorHovered
                : defaultLayoutStyle?.inputStyle.buttonColor?.colorHovered;

        cssProperties.tryAdd("--search-bar-input-icon-color", iconColor);
        cssProperties.tryAdd("--search-bar-input-icon-background-color", buttonColorDefault);
        cssProperties.tryAdd("--search-bar-input-icon-background-color-hovered", buttonColorHovered);
    }

    private setInputColorVariables(cssProperties: CssProperties, data: ISearchBarWidgetData): void {
        cssProperties.tryAdd("--search-bar-input-background-color", data.style.inputStyle.backgroundColor.colorDefault);
        cssProperties.tryAdd("--search-bar-input-background-color-hovered", data.style.inputStyle.backgroundColor.colorHovered);

        cssProperties.tryAdd("--search-bar-input-border-color", data.style.inputStyle.borderColor.colorDefault);
        cssProperties.tryAdd("--search-bar-input-border-color-hovered", data.style.inputStyle.borderColor.colorHovered);

        cssProperties.tryAdd("--search-bar-input-placeholder-color", data.style.inputStyle.placeholderColor.colorDefault);
        cssProperties.tryAdd("--search-bar-input-placeholder-color-hovered", data.style.inputStyle.placeholderColor.colorHovered);

        cssProperties.tryAdd("--search-bar-input-border-width", `${data.style.inputStyle.borderThickness}px`);
        cssProperties.tryAdd("--search-bar-input-border-top-left-radius", `${data.style.inputStyle.borderRadius.topLeftPx}px`);
        cssProperties.tryAdd("--search-bar-input-border-top-right-radius", `${data.style.inputStyle.borderRadius.topRightPx}px`);
        cssProperties.tryAdd("--search-bar-input-border-bottom-left-radius", `${data.style.inputStyle.borderRadius.bottomLeftPx}px`);
        cssProperties.tryAdd("--search-bar-input-border-bottom-right-radius", `${data.style.inputStyle.borderRadius.bottomRightPx}px`);

        cssProperties.tryAdd("--search-bar-input-font-family", data.style.inputStyle.textFont.family);
        cssProperties.tryAdd("--search-bar-input-font-weight", data.style.inputStyle.textFont.weight);
        cssProperties.tryAdd("--search-bar-input-font-color", data.style.inputStyle.textFont.color);
    }

    private resetLayoutSetting(field: HTMLDivElement): void {
        field.classList.remove("without-icon", "icon-start", "icon-end", "filled-icon-start", "filled-icon-end");
    }

    private setLayoutStyle(layout: SearchBarLayoutType, field: HTMLDivElement): void {
        switch (layout) {
            case "without-icon":
                field.classList.add("without-icon");
                break;

            case "icon-start":
                field.classList.add("icon-start");
                break;

            case "icon-end":
                field.classList.add("icon-end");
                break;

            case "filled-icon-start":
                field.classList.add("filled-icon-start");
                break;

            case "filled-icon-end":
                field.classList.add("filled-icon-end");
                break;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        return;
    }

    protected getContentSelectorsInternal(element: HTMLElement): string[] {
        return [`#${element.id}`, `#${element.id} .search-field`];
    }

}
